import axios from 'axios';
import { apiDelete, apiGet, apiPost } from './axios';
import {
  computeSha256,
  FILE_IN_USE_MESSAGE,
  inferContentType,
  isUnreadableFileError,
  snapshotLocalFile
} from 'src/features/uploads/utils';

// ---------------------------------------------------------------------------
// Upload batch API (direct-to-blob flow)
// ---------------------------------------------------------------------------

export type UploadFileStatus =
  | 'pending_upload'
  | 'uploaded'
  | 'security_scan_pending'
  | 'security_scanning'
  | 'clean'
  | 'infected'
  | 'scan_failed'
  | 'rejected'
  | 'ready'
  | 'failed'
  | 'cancelled';

export type UploadBatchStatus = 'open' | 'processing' | 'complete' | 'failed';

export interface UploadFileIntent {
  name: string;
  size: number;
  contentType: string;
  sha256: string;
}

export interface UploadSasInfo {
  file_id: string;
  blob_name: string;
  upload_url: string;
  expiry: string;
}

export interface RejectedUploadFile {
  name: string;
  detail: string;
}

export interface CreateUploadBatchResponse {
  batch_id: string;
  files: UploadSasInfo[];
  rejected?: RejectedUploadFile[];
}

export interface UploadFileItem {
  id: string;
  batch_id: string;
  name: string;
  size: number;
  content_type: string;
  sha256: string;
  status: UploadFileStatus;
  error_message?: string;
  created_at?: string;
}

export interface UploadBatch {
  id: string;
  status: UploadBatchStatus;
  total: number;
  uploaded: number;
  ready: number;
  failed: number;
  created_at?: string;
}

export interface CreateUploadBatchOptions {
  groupId?: string;
  pamQuestionId?: string;
  pamSiteId?: string;
  /**
   * When true, uploaded files are added to the organisation library so they
   * become reusable organisational documents (visible to the whole tenant).
   * Available to any non-guest member. Overrides documentType server-side.
   */
  addToOrganisation?: boolean;
}

export const uploadBatchApi = {
  /**
   * Create a new upload batch. Returns per-file SAS write URLs.
   *
   * @param files        File metadata for validation and SAS URL generation.
   * @param documentType Optional document_type tag. Use 'tenant_file' for
   *                     organisation uploads, 'reviewed_file' for reviews,
   *                     'pam_content_feedback' for PAM content feedback, etc.
   * @param options      Optional upload target context (group share, PAM evidence).
   */
  createBatch: async (
    files: UploadFileIntent[],
    documentType?: string,
    options?: CreateUploadBatchOptions
  ): Promise<CreateUploadBatchResponse> => {
    const { data } = await apiPost<CreateUploadBatchResponse>('/files/upload/batch', {
      files: files.map((f) => ({
        name: f.name,
        size: f.size,
        content_type: f.contentType,
        sha256: f.sha256
      })),
      ...(documentType ? { document_type: documentType } : {}),
      ...(options?.groupId ? { group_id: options.groupId } : {}),
      ...(options?.pamQuestionId ? { pam_question_id: options.pamQuestionId } : {}),
      ...(options?.pamSiteId ? { pam_site_id: options.pamSiteId } : {}),
      ...(options?.addToOrganisation ? { add_to_organisation: true } : {})
    });
    return data;
  },

  /**
   * Upload a single file directly to Azure Blob Storage using its SAS URL.
   * This bypasses FastAPI entirely.
   */
  uploadToBlob: async (
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const body = await snapshotLocalFile(file);
    try {
      await axios.put(uploadUrl, body, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': body.type || file.type
        },
        signal,
        onUploadProgress: (evt) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
        // Skip the tenant-prefixing interceptor since this is an absolute Azure URL
        skipTenantPrefix: true
      });
    } catch (error) {
      if (isUnreadableFileError(error)) {
        throw new Error(FILE_IN_USE_MESSAGE);
      }
      throw error;
    }
  },

  /**
   * Notify the backend that a single file has been uploaded to Blob Storage.
   * This triggers verification and the security scan pipeline.
   */
  completeFileUpload: async (fileId: string): Promise<UploadFileItem> => {
    const { data } = await apiPost<UploadFileItem>(`/files/upload/${fileId}/complete`);
    return data;
  },

  /**
   * Notify the backend that all successfully uploaded files are ready for processing.
   * Sends a single request instead of one-per-file to avoid rate limiting.
   * The response is 204; poll ``getBatchFiles`` to wait for ``ready`` status.
   */
  completeBatchUploads: async (batchId: string, fileIds: string[]): Promise<void> => {
    await apiPost(`/files/upload/batch/${batchId}/complete`, { file_ids: fileIds });
  },

  /**
   * Poll batch progress counters.
   */
  getBatch: async (batchId: string): Promise<UploadBatch> => {
    const { data } = await apiGet<UploadBatch>(`/files/upload/batch/${batchId}`);
    return data;
  },

  /**
   * Get the full file list for a batch.
   */
  getBatchFiles: async (batchId: string): Promise<UploadFileItem[]> => {
    const { data } = await apiGet<UploadFileItem[]>(`/files/upload/batch/${batchId}/files`);
    return data;
  },

  /**
   * Cancel an upload batch. Cancels every file that is not already ready and
   * deletes its blobs.
   * Safe to call multiple times (idempotent).
   */
  cancelBatch: async (batchId: string): Promise<void> => {
    await apiDelete(`/files/upload/batch/${batchId}`);
  },

  /** Cancel one file that has not yet been promoted. */
  cancelFile: async (fileId: string): Promise<void> => {
    await apiDelete(`/files/upload/file/${fileId}`, { meta: { globalError: false } });
  }
};

// ---------------------------------------------------------------------------
// Analysis status
// ---------------------------------------------------------------------------

/**
 * Fetch the current analysis status for a hub document.
 * The ``analysis_status`` field progresses through: pending → running →
 * tagging → completed (or failed).
 */
export async function getDocumentAnalysisSummary(documentId: string): Promise<{ analysis_status: string | null }> {
  const { data } = await apiGet<{ analysis_status: string | null }>(`/files/analysis/${documentId}/summary`, {
    meta: { globalError: false, context: 'analysis-status' }
  });
  return { analysis_status: data.analysis_status ?? null };
}

// ---------------------------------------------------------------------------
// Shared hub-upload utilities
// ---------------------------------------------------------------------------

/** Statuses that mean the file will not progress further (failed states). */
export const HUB_FAILED_STATUSES = new Set<UploadFileStatus>([
  'infected',
  'scan_failed',
  'rejected',
  'failed',
  'cancelled'
]);

/** Analysis statuses that mean DI has finished (result is cached). */
const DI_COMPLETE_STATUSES = new Set(['tagging', 'completed', 'failed']);

/**
 * Poll the analysis summary until Document Intelligence finishes (or the
 * timeout elapses). Resolves once DI is complete; the caller can then call
 * the writing/chat registration endpoint and expect a cached result.
 *
 * Proceeds silently after timeout — the backend will re-run DI on demand
 * if the cached result is missing.
 */
export async function waitForDocumentIntelligence(
  documentId: string,
  signal: AbortSignal,
  options?: { pollIntervalMs?: number; timeoutMs?: number }
): Promise<void> {
  const { pollIntervalMs = 3_000, timeoutMs = 3 * 60 * 1000 } = options ?? {};
  const deadline = Date.now() + timeoutMs;
  while (!signal.aborted && Date.now() < deadline) {
    await new Promise((res) => setTimeout(res, pollIntervalMs));
    if (signal.aborted) break;
    try {
      const { analysis_status } = await getDocumentAnalysisSummary(documentId);
      if (analysis_status && DI_COMPLETE_STATUSES.has(analysis_status)) return;
    } catch {
      // swallow transient errors
    }
  }
  // Proceed anyway — backend handles missing DI result on demand
}

/**
 * Poll a batch until the given file reaches ``ready`` or a terminal error
 * status. Returns the final status.
 *
 * Intended for use after ``completeBatchUploads`` so callers know when the
 * file is promoted and safe to use as a hub document.
 */
export async function waitForHubReady(
  batchId: string,
  fileId: string,
  signal: AbortSignal,
  options?: { pollIntervalMs?: number; timeoutMs?: number }
): Promise<UploadFileStatus> {
  // Defender polling can take up to 200 seconds server-side, before promotion.
  const { pollIntervalMs = 2500, timeoutMs = 5 * 60 * 1000 } = options ?? {};
  const deadline = Date.now() + timeoutMs;
  while (!signal.aborted && Date.now() < deadline) {
    await new Promise((res) => setTimeout(res, pollIntervalMs));
    if (signal.aborted) break;
    try {
      const files = await uploadBatchApi.getBatchFiles(batchId);
      const file = files.find((f) => f.id === fileId);
      if (!file) continue;
      if (file.status === 'ready' || HUB_FAILED_STATUSES.has(file.status)) {
        return file.status;
      }
    } catch {
      // swallow transient errors
    }
  }
  return 'failed';
}

// ---------------------------------------------------------------------------
// Convenience helper — single-file upload through the hub pipeline
// ---------------------------------------------------------------------------

/**
 * Upload a single file through the Document Hub pipeline (hash → batch →
 * blob upload → completeBatch → waitForHubReady) and return its document ID
 * (ULID) once the file is promoted and ready.
 *
 * Throws if the upload fails or the file is rejected by the security scan.
 *
 * Use this for one-off imperative uploads where you need the document ID
 * immediately (e.g. certificates, appointment letters).
 */
export async function uploadFileToHub(
  file: File,
  options?: { documentType?: string; signal?: AbortSignal; addToOrganisation?: boolean }
): Promise<string> {
  const { documentType = 'upload', signal, addToOrganisation = false } = options ?? {};
  const localFile = await snapshotLocalFile(file);
  const contentType = inferContentType(localFile);
  const sha256 = await computeSha256(localFile);

  const batch = await uploadBatchApi.createBatch(
    [{ name: localFile.name, size: localFile.size, contentType, sha256 }],
    documentType,
    { addToOrganisation }
  );

  const sasInfo = batch.files[0];
  await uploadBatchApi.uploadToBlob(sasInfo.upload_url, localFile);

  if (signal?.aborted) throw new Error('Upload cancelled');

  await uploadBatchApi.completeBatchUploads(batch.batch_id, [sasInfo.file_id]);

  const ctrl = new AbortController();
  if (signal) {
    signal.addEventListener('abort', () => ctrl.abort());
  }

  const status = await waitForHubReady(batch.batch_id, sasInfo.file_id, ctrl.signal);
  if (HUB_FAILED_STATUSES.has(status)) {
    throw new Error(
      status === 'infected' ? 'File was rejected by the security scan.' : `File processing failed (status: ${status})`
    );
  }

  return sasInfo.file_id;
}
